import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@akuna/ui";
import { Button } from "@akuna/ui";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useCreateMedication } from "@/hooks/useMedications";
import { useMedications } from "@/hooks/useMedications";
import { MedicationAutocomplete, type MedicationWithRelations } from "./MedicationAutocomplete";
import type { Database } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, X, CheckCircle2 } from "lucide-react";
import { Alert, AlertDescription } from "@akuna/ui";

type MedicationRow = Database["public"]["Tables"]["medications"]["Row"];

const createMedicationSchema = z.object({
  name: z.string().min(2),
  type: z.enum(['principio_ativo', 'product']),
  activePrincipleIds: z.array(z.string()).optional(),
  manufacturer: z.string().optional(),
});

type CreateMedicationFormValues = z.infer<typeof createMedicationSchema>;

interface CreateMedicationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialName?: string;
  onMedicationCreated?: (medication: MedicationRow) => void;
}

export function CreateMedicationDialog({
  open,
  onOpenChange,
  initialName = "",
  onMedicationCreated,
}: CreateMedicationDialogProps) {
  const { t } = useTranslation("prescriptions");
  const createMedication = useCreateMedication();
  const queryClient = useQueryClient();
  const [activePrincipleSearch, setActivePrincipleSearch] = useState("");
  const [selectedActivePrincipleIds, setSelectedActivePrincipleIds] = useState<string[]>([]);
  const [createdMedication, setCreatedMedication] = useState<MedicationRow | null>(null);
  const [createdActivePrinciples, setCreatedActivePrinciples] = useState<Array<{ id: string; name: string }>>([]);

  const form = useForm<CreateMedicationFormValues>({
    resolver: zodResolver(createMedicationSchema),
    defaultValues: {
      name: initialName,
      type: 'product',
      activePrincipleIds: [],
      manufacturer: undefined,
    },
  });

  const medicationType = form.watch("type");

  // Buscar princípios ativos quando tipo for produto
  const { data: activePrinciples = [], isLoading: isLoadingActivePrinciples } = useMedications({
    type: 'principio_ativo',
    searchTerm: activePrincipleSearch.length >= 2 ? activePrincipleSearch : undefined,
  });

  // Reset form quando o modal abre ou initialName muda
  useEffect(() => {
    if (open) {
      form.reset({
        name: initialName,
        type: 'product',
        activePrincipleIds: [],
        manufacturer: undefined,
      });
      setActivePrincipleSearch("");
      setSelectedActivePrincipleIds([]);
      setCreatedMedication(null);
      setCreatedActivePrinciples([]);
    }
  }, [open, initialName, form]);

  // Mapear IDs selecionados para objetos de medicamento para exibição
  const selectedActivePrinciples = useMemo(() => {
    return activePrinciples.filter(ap => selectedActivePrincipleIds.includes(ap.id));
  }, [activePrinciples, selectedActivePrincipleIds]);

  // Função para criar princípio ativo diretamente quando clicar no botão no dropdown
  const handleCreateActivePrinciple = async (name: string) => {
    try {
      const trimmedName = name.trim();
      if (!trimmedName) return;

      // Verificar se já existe
      const searchTerm = trimmedName.toLowerCase();
      let existingPrinciple = activePrinciples.find(
        ap => ap.name.toLowerCase() === searchTerm
      );

      // Se não encontrou na lista filtrada, buscar diretamente no banco
      if (!existingPrinciple) {
        const { data: allActivePrinciples } = await supabase
          .from("medications")
          .select("id, name")
          .eq("type", "principio_ativo")
          .ilike("name", trimmedName);

        existingPrinciple = allActivePrinciples?.find(
          ap => ap.name.toLowerCase() === searchTerm
        ) as MedicationRow | undefined;
      }

      if (existingPrinciple) {
        // Se já existe, adicionar à lista se não estiver
        if (!selectedActivePrincipleIds.includes(existingPrinciple.id)) {
          const newIds = [...selectedActivePrincipleIds, existingPrinciple.id];
          setSelectedActivePrincipleIds(newIds);
          form.setValue("activePrincipleIds", newIds);
          setActivePrincipleSearch("");
          toast.success(t("createMedication.activePrincipleAdded", { name: existingPrinciple.name }));
        } else {
          toast.info(t("createMedication.activePrincipleAlreadySelected"));
        }
        return;
      }

      // Criar novo princípio ativo
      toast.loading(t("createMedication.creatingActivePrinciple", { name: trimmedName }), { id: 'creating-principle-direct' });
      const newActivePrinciple = await createMedication.mutateAsync({
        name: trimmedName,
        type: 'principio_ativo',
      });

      if (newActivePrinciple) {
        toast.dismiss('creating-principle-direct');
        toast.success(t("createMedication.createdActivePrinciple", { name: newActivePrinciple.name }));
        
        // Adicionar à lista de selecionados
        const newIds = [...selectedActivePrincipleIds, newActivePrinciple.id];
        setSelectedActivePrincipleIds(newIds);
        form.setValue("activePrincipleIds", newIds);
        
        // Limpar campo de busca
        setActivePrincipleSearch("");
        
        // Invalidar queries para atualizar a lista
        await queryClient.invalidateQueries({ queryKey: ["medications"] });
      }
    } catch (error: unknown) {
      toast.dismiss('creating-principle-direct');
      const supabaseError = error as { code?: string; message?: string } | null;
      if (supabaseError && supabaseError.code !== '23505') {
        toast.error(t("createMedication.createActivePrincipleError"));
      }
      // Erro 23505 já é tratado no hook
    }
  };

  const onSubmit = async (data: CreateMedicationFormValues) => {
    try {
      let activePrincipleIdsToUse = data.activePrincipleIds || [];
      const newlyCreatedPrinciples: MedicationRow[] = [];

      // Se for produto e houver texto digitado no autocomplete, criar novo princípio ativo
      if (data.type === 'product' && activePrincipleSearch.trim()) {
        const searchTerm = activePrincipleSearch.trim().toLowerCase();
        
        // Verificar se já existe na lista de resultados da busca
        let existingPrinciple = activePrinciples.find(
          ap => ap.name.toLowerCase() === searchTerm
        );

        // Se não encontrou na lista filtrada, buscar diretamente no banco para verificar se existe
        if (!existingPrinciple) {
          const trimmedName = activePrincipleSearch.trim();
          const { data: allActivePrinciples } = await supabase
            .from("medications")
            .select("id, name")
            .eq("type", "principio_ativo")
            .ilike("name", trimmedName);

          // Buscar exatamente pelo nome (case-insensitive)
          existingPrinciple = allActivePrinciples?.find(
            ap => ap.name.toLowerCase() === searchTerm
          ) as MedicationRow | undefined;
        }

        if (existingPrinciple) {
          // Se já existe, adicionar à lista se não estiver
          if (!activePrincipleIdsToUse.includes(existingPrinciple.id)) {
            activePrincipleIdsToUse = [...activePrincipleIdsToUse, existingPrinciple.id];
          }
        } else {
          // Criar novo princípio ativo usando o hook
          toast.loading(t("createMedication.creatingActivePrinciple", { name: activePrincipleSearch.trim() }), { id: 'creating-principle' });
          const newActivePrinciple = await createMedication.mutateAsync({
            name: activePrincipleSearch.trim(),
            type: 'principio_ativo',
          });

          if (newActivePrinciple) {
            toast.dismiss('creating-principle');
            toast.success(t("createMedication.createdActivePrinciple", { name: newActivePrinciple.name }), { id: 'creating-principle' });
            activePrincipleIdsToUse = [...activePrincipleIdsToUse, newActivePrinciple.id];
            newlyCreatedPrinciples.push(newActivePrinciple);
          }
        }
      }

      // Validar que produto precisa de pelo menos um princípio ativo
      if (data.type === 'product' && activePrincipleIdsToUse.length === 0) {
        form.setError("activePrincipleIds", {
          type: "manual",
          message: t("createMedication.activePrincipleRequired"),
        });
        return;
      }

      // Criar o medicamento
      toast.loading(t("createMedication.creatingMedication", { type: data.type === 'principio_ativo' ? t("createMedication.typeActivePrinciple") : t("createMedication.typeProduct") }), { id: 'creating-medication' });
      const created = await createMedication.mutateAsync({
        name: data.name.trim(),
        type: data.type,
        manufacturer: data.manufacturer?.trim() || null,
        activePrincipleIds: activePrincipleIdsToUse.length > 0 ? activePrincipleIdsToUse : undefined,
      });

      if (created) {
        toast.dismiss('creating-medication');
        // Buscar os princípios ativos criados/selecionados para mostrar no resumo
        if (activePrincipleIdsToUse.length > 0) {
          const { data: principlesData } = await supabase
            .from("medications")
            .select("id, name")
            .in("id", activePrincipleIdsToUse);

          setCreatedActivePrinciples((principlesData || []) as Array<{ id: string; name: string }>);
        }
        setCreatedMedication(created);
        // Invalidar todas as queries de medicamentos para atualizar as listas
        await queryClient.invalidateQueries({ queryKey: ["medications"] });
        onMedicationCreated?.(created);
        // Não fechar o modal imediatamente - mostrar resumo primeiro
      }
    } catch (error: unknown) {
      console.error("Erro ao criar medicamento:", error);
      // O erro de duplicata já é tratado no hook, mas podemos tratar outros erros aqui se necessário
      const supabaseError = error as { code?: string; message?: string } | null;
      if (supabaseError && supabaseError.code !== '23505') {
        toast.error(t("createMedication.createMedicationError"));
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("createMedication.title")}</DialogTitle>
          <DialogDescription>
            {t("createMedication.description")}
          </DialogDescription>
        </DialogHeader>

        {createdMedication ? (
          <div className="space-y-4">
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <div className="space-y-2">
                  <p className="font-semibold">{t("success", { ns: "common" })}!</p>
                  <div className="space-y-1 text-sm">
                    <p>
                      <span className="font-medium">{t("name", { ns: "common" })}:</span> {createdMedication.name}
                    </p>
                    {createdMedication.type === 'product' && createdActivePrinciples.length > 0 && (
                      <div>
                        <span className="font-medium">{t("createMedication.activePrinciples")}:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {createdActivePrinciples.map((principle) => (
                            <Badge key={principle.id} variant="secondary" className="text-xs">
                              {principle.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {createdMedication.manufacturer && (
                      <p>
                        <span className="font-medium">{t("createMedication.manufacturer")}:</span> {createdMedication.manufacturer}
                      </p>
                    )}
                  </div>
                </div>
              </AlertDescription>
            </Alert>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  // Garantir que as queries sejam atualizadas antes de fechar
                  await queryClient.invalidateQueries({ queryKey: ["medications"] });
                  onOpenChange(false);
                  setCreatedMedication(null);
                  setCreatedActivePrinciples([]);
                }}
              >
                {t("close", { ns: "common" })}
              </Button>
            </div>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("createMedication.medicationName")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("createMedication.medicationNamePlaceholder")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("createMedication.medicationType")}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="flex flex-col space-y-1"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="principio_ativo" id="principio_ativo" />
                        <Label htmlFor="principio_ativo" className="font-normal cursor-pointer">
                          {t("createMedication.typeActivePrinciple")}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="product" id="product" />
                        <Label htmlFor="product" className="font-normal cursor-pointer">
                          {t("createMedication.typeProduct")}
                        </Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {medicationType === 'product' && (
              <>
                <FormField
                  control={form.control}
                  name="activePrincipleIds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("createMedication.activePrinciples")}</FormLabel>
                      <FormControl>
                        <div className="space-y-2">
                          <MedicationAutocomplete
                            medications={activePrinciples.filter(ap => !selectedActivePrincipleIds.includes(ap.id)) as MedicationWithRelations[]}
                            isLoading={isLoadingActivePrinciples}
                            value={activePrincipleSearch}
                            selectedMedicationId={undefined}
                            popoverPortalled={false}
                            onChange={(value) => {
                              setActivePrincipleSearch(value);
                            }}
                            onSelectMedication={(medication) => {
                              if (!selectedActivePrincipleIds.includes(medication.id)) {
                                const newIds = [...selectedActivePrincipleIds, medication.id];
                                setSelectedActivePrincipleIds(newIds);
                                field.onChange(newIds);
                                setActivePrincipleSearch("");
                              }
                            }}
                            onCreateNew={handleCreateActivePrinciple}
                            placeholder={t("createMedication.activePrinciplesPlaceholder")}
                          />
                          
                          {selectedActivePrinciples.length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-2">
                              {selectedActivePrinciples.map((principle) => (
                                <Badge
                                  key={principle.id}
                                  variant="secondary"
                                  className="flex items-center gap-1 pr-1"
                                >
                                  <span>{principle.name}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newIds = selectedActivePrincipleIds.filter(id => id !== principle.id);
                                      setSelectedActivePrincipleIds(newIds);
                                      field.onChange(newIds);
                                    }}
                                    className="ml-1 rounded-full hover:bg-destructive/20 p-0.5"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="manufacturer"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("createMedication.manufacturer")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("createMedication.manufacturerPlaceholder")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("cancel", { ns: "common" })}
              </Button>
              <Button
                type="submit"
                disabled={createMedication.isPending}
              >
                {createMedication.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t("createMedication.title")}
              </Button>
            </div>
          </form>
        </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
